import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { EvtSectionHeader } from "components/header";
import { EvtStatsCard } from "components/stats/display-card";
import { EvtStatsCardPlaceholder } from "components/stats/placeholder";
import { EvtLockedSection } from "components/stats/locked-section";
import { DisplayPercentage } from "components/display/percentage";
import { DisplayAmount } from "components/display/amount";
import { DisplayHours } from "components/display/hours";
import { DisplayDays } from "components/display/days";
import QueryError from "components/query-error";
import { useFetch } from "@/hooks/useFetch";
import { useContract } from "../context";
import { fetchContractRevenueCalculation } from "../api";
import { usePermissions } from "contexts/permission";
import { DASHBOARD_STATS_INFO } from "./constants";
import {
  DASHBOARD_VALIDATION_TITLE,
  getDashboardValidationIssues,
} from "@/modules/work/shared/dashboard-validation";
import { BillableHoursWarning } from "@/modules/work/shared/billable-hours-warning";
import { JobTagsFilterCombobox } from "@/modules/work/shared/job-tag-filter";

const ContractStatsPlaceholder = ({ canReadAmount }) => (
  <>
    <section>
      <EvtSectionHeader title="Performance" />
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6">
        <EvtStatsCardPlaceholder />
        <EvtStatsCardPlaceholder />
      </div>
    </section>
    {canReadAmount && (
      <section>
        <EvtSectionHeader title="Active Billables Status" />
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6">
          <EvtStatsCardPlaceholder />
          <EvtStatsCardPlaceholder />
        </div>
      </section>
    )}
    {canReadAmount && (
      <section>
        <EvtSectionHeader title="Revenue" />
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6">
          <EvtStatsCardPlaceholder />
          <EvtStatsCardPlaceholder />
          <EvtStatsCardPlaceholder />
          <EvtStatsCardPlaceholder />
          <EvtStatsCardPlaceholder />
          <EvtStatsCardPlaceholder />
        </div>
      </section>
    )}
    <section>
      <EvtSectionHeader title="Delivery" />
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6">
        <EvtStatsCardPlaceholder />
      </div>
    </section>
  </>
);
// Holds the job tag filter outside the stats so it keeps its value across loading/error states
export const ContractDashboardStatsSection = () => {
  const { type } = useContract();
  const [jobTagId, setJobTagId] = useState();
  if (type === "free") return null;
  return (
    <>
      {/* <div className="flex justify-end px-4 md:px-6 mt-3.5">
        <JobTagsFilterCombobox value={jobTagId} onChange={setJobTagId} />
      </div> */}
      <ContractStats jobTagId={jobTagId} />
    </>
  );
};

const ContractStats = ({ jobTagId }) => {
  const { contractId } = useParams();
  const { type } = useContract();
  const permissions = usePermissions();

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  if (type === "free") {
    return null;
  }

  const { data, isLoading, isError, refetch } = useFetch({
    queryKey: ["revenue-calculation-contract-dashboard", contractId, jobTagId],
    queryFn: () => fetchContractRevenueCalculation({ contractId, jobTagId }),
    enabled: !!contractId,
  });

  if (isLoading) {
    return <ContractStatsPlaceholder canReadAmount={canReadAmount} />;
  }
  const validationIssues = getDashboardValidationIssues(
    data?.validation,
    "contract",
    contractId,
  );
  if (validationIssues.length) {
    return (
      <EvtLockedSection
        title={DASHBOARD_VALIDATION_TITLE}
        items={validationIssues}
      >
        {/* Placeholder values only: real stats are never rendered while invalid. */}
        <ContractStatsPlaceholder canReadAmount={canReadAmount} />
      </EvtLockedSection>
    );
  }
  if (isError) {
    return (
      <>
        <section className="mt-3.5">
          <EvtSectionHeader title="Performance" />
          <div className="px-4 md:px-6 mt-2.5">
            <QueryError
              message="Failed to load dashboard metrics."
              onRetry={refetch}
            />
          </div>
        </section>
        {canReadAmount && (
          <section className="mt-3.5">
            <EvtSectionHeader title="Active Billables Status" />
            <div className="px-4 md:px-6 mt-2.5">
              <QueryError
                message="Failed to load dashboard metrics."
                onRetry={refetch}
              />
            </div>
          </section>
        )}
        {canReadAmount && (
          <section className="mt-3.5">
            <EvtSectionHeader title="Revenue" />
            <div className="px-4 md:px-6 mt-2.5">
              <QueryError
                message="Failed to load dashboard metrics."
                onRetry={refetch}
              />
            </div>
          </section>
        )}
        <section className="mt-3.5">
          <EvtSectionHeader title="Delivery" />
          <div className="px-4 md:px-6 mt-2.5">
            <QueryError
              message="Failed to load dashboard metrics."
              onRetry={refetch}
            />
          </div>
        </section>
      </>
    );
  }

  const info = DASHBOARD_STATS_INFO[type] || {};

  const renderFixedCards = (stats) => ({
    performance: (
      <>
        <EvtStatsCard
          label="Contract Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={info?.budget_vs_archieved?.info}
        />
        {canReadAmount ? (
          <EvtStatsCard
            label="Accrued Net Margin"
            value={
              <DisplayPercentage
                value={stats?.accrued_net_margin}
                hoursOnly
                withSuffix
              />
            }
            info={info?.accrued_net_margin?.info}
          />
        ) : null}
      </>
    ),
    revenue: (
      <>
        <EvtStatsCard
          label="Billable"
          value={<DisplayAmount value={stats?.billable_revenue} />}
          description={
            <>
              Billable hours • <DisplayHours value={stats?.billable_hours} />
            </>
          }
          info={info?.billable_revenue?.info}
        />
        <EvtStatsCard
          label="Billed"
          value={<DisplayAmount value={stats?.billed_revenue} />}
          description={
            <>
              Billed hours • <DisplayHours value={stats?.billed_hours} />
            </>
          }
          info={info?.billed_revenue?.info}
        />
        <EvtStatsCard
          label="Remaining Budget"
          value={<DisplayAmount value={stats?.remaining_budget} />}
          info={info?.remaining_budget?.info}
        />
        <EvtStatsCard
          label="Inventory"
          value={<DisplayAmount value={stats?.inventory} />}
          info={info?.inventory?.info}
        />
        <EvtStatsCard
          label="Prepaid"
          value={<DisplayAmount value={stats?.prepaid} />}
          info={info?.prepaid?.info}
        />
        <EvtStatsCard
          label="Backlog"
          value={<DisplayAmount value={stats?.backlog} />}
          info={info?.backlog?.info}
        />
      </>
    ),
    delivery: (
      <>
        <EvtStatsCard
          label="Allocated Hours"
          value={<DisplayHours value={stats?.allocated_hours} />}
          info={info?.allocated_hours?.info}
        />
        <EvtStatsCard
          label="Spent vs Consumed"
          value={
            <>
              <DisplayHours value={stats?.spent_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.consumed_hours} hoursOnly />
            </>
          }
          info={info?.spent_vs_consumed?.info}
        />
      </>
    ),
  });

  const renderHourlyCards = (stats) => ({
    performance: (
      <>
        <EvtStatsCard
          label="Contract Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={info?.budget_vs_archieved?.info}
        />
      </>
    ),
    revenue: (
      <>
        <EvtStatsCard
          label="Billable"
          value={<DisplayAmount value={stats?.billable_revenue} />}
          description={
            <>
              Billable hours • <DisplayHours value={stats?.billable_hours} />
            </>
          }
          info={info?.billable_revenue?.info}
        />
        <EvtStatsCard
          label="Billed"
          value={<DisplayAmount value={stats?.billed_revenue} />}
          description={
            <>
              Billed hours • <DisplayHours value={stats?.billed_hours} />
            </>
          }
          info={info?.billed_revenue?.info}
        />
        <EvtStatsCard
          label="Remaining Budget"
          value={<DisplayAmount value={stats?.remaining_budget} />}
          info={info?.remaining_budget?.info}
        />
        <EvtStatsCard
          label="Inventory"
          value={<DisplayAmount value={stats?.inventory} />}
          info={info?.inventory?.info}
        />
        <EvtStatsCard
          label="Prepaid"
          value={<DisplayAmount value={stats?.prepaid} />}
          info={info?.prepaid?.info}
        />
        <EvtStatsCard
          label="Backlog"
          value={<DisplayAmount value={stats?.backlog} />}
          info={info?.backlog?.info}
        />
      </>
    ),
    delivery: (
      <>
        <EvtStatsCard
          label="Approved vs Logged"
          value={
            <>
              <DisplayHours value={stats?.approved_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.logged_hours} hoursOnly />
            </>
          }
          info={info?.approved_vs_logged?.info}
        />
      </>
    ),
  });

  const renderDedicatedCards = (stats) => ({
    performance: (
      <>
        <EvtStatsCard
          label="Contract Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={info?.budget_vs_archieved?.info}
        />
      </>
    ),
    activebillablesstatus: (
      <>
        <EvtStatsCard
          label="Billables Amount"
          value={<DisplayAmount value={stats?.active_billable_amount} />}
          info={info?.active_billable_amount?.info}
        />
        <EvtStatsCard
          label="Billables Net Cost"
          value={<DisplayAmount value={stats?.active_billable_net_cost} />}
          info={info?.active_billable_net_cost?.info}
        />
      </>
    ),
    revenue: (
      <>
        <EvtStatsCard
          label="Billable"
          value={<DisplayAmount value={stats?.billable_revenue} />}
          description={
            <>
              Billable period • <DisplayDays value={stats?.billable_period} />
            </>
          }
          info={info?.billable_revenue?.info}
        />
        <EvtStatsCard
          label="Billed"
          value={<DisplayAmount value={stats?.billed_revenue} />}
          description={
            <>
              Billed period • <DisplayDays value={stats?.billed_period} />
            </>
          }
          info={info?.billed_revenue?.info}
        />
        <EvtStatsCard
          label="Remaining Budget"
          value={<DisplayAmount value={stats?.remaining_budget} />}
          info={info?.remaining_budget?.info}
        />
        <EvtStatsCard
          label="Prepaid"
          value={<DisplayAmount value={stats?.prepaid} />}
          info={info?.prepaid?.info}
        />
        <EvtStatsCard
          label="Inventory"
          value={<DisplayAmount value={stats?.inventory} />}
          info={info?.inventory?.info}
        />
        <EvtStatsCard
          label="Backlog"
          value={<DisplayAmount value={stats?.backlog} />}
          info={info?.backlog?.info}
        />
      </>
    ),
    delivery: (
      <>
        <EvtStatsCard
          label="Spent vs Consumed"
          value={
            <>
              <DisplayHours value={stats?.spent_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.consumed_hours} hoursOnly />
            </>
          }
          info={info?.spent_vs_consumed?.info}
        />
      </>
    ),
  });

  const renderBucketCards = (stats) => ({
    performance: (
      <>
        <EvtStatsCard
          label="Contract Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={info?.budget_vs_archieved?.info}
        />
      </>
    ),
    revenue: (
      <>
        <EvtStatsCard
          label="Bucket Value"
          value={<DisplayAmount value={stats?.bucket_value} />}
          description={
            <>
              Bucket Size • <DisplayHours value={stats?.bucket_size} />
            </>
          }
          info={info?.bucket_value?.info}
        />
        <EvtStatsCard
          label="Billable"
          value={<DisplayAmount value={stats?.billable_revenue} />}
          description={
            <>
              Billable hours • <DisplayHours value={stats?.billable_hours} />
            </>
          }
          info={info?.billable_revenue?.info}
        />
        <EvtStatsCard
          label="Billed"
          value={<DisplayAmount value={stats?.billed_revenue} />}
          description={
            <>
              Billed hours • <DisplayHours value={stats?.billed_hours} />
            </>
          }
          info={info?.billed_revenue?.info}
        />
        <EvtStatsCard
          label="Remaining Budget"
          value={<DisplayAmount value={stats?.remaining_budget} />}
          info={info?.remaining_budget?.info}
        />
        <EvtStatsCard
          label="Inventory"
          value={<DisplayAmount value={stats?.inventory} />}
          description={
            <>
              Bill for target margin •{" "}
              <DisplayHours value={stats?.target_hours} />
            </>
          }
          info={info?.inventory?.info}
        />
        <EvtStatsCard
          label="Prepaid"
          value={<DisplayAmount value={stats?.prepaid} />}
          info={info?.prepaid?.info}
        />
        <EvtStatsCard
          label="Backlog"
          value={<DisplayAmount value={stats?.backlog} />}
          info={info?.backlog?.info}
        />
      </>
    ),
    delivery: (
      <>
        <EvtStatsCard
          label="Approved vs Logged"
          value={
            <>
              <DisplayHours value={stats?.approved_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.logged_hours} hoursOnly />
            </>
          }
          info={info?.approved_vs_logged?.info}
        />
      </>
    ),
  });

  const renderRecurringCards = (stats) => ({
    performance: (
      <>
        <EvtStatsCard
          label="Contract Health"
          value={
            <DisplayPercentage
              value={stats?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={info?.budget_vs_archieved?.info}
        />
      </>
    ),
    activebillablesstatus: (
      <>
        <EvtStatsCard
          label="Billables Amount"
          value={<DisplayAmount value={stats?.active_billable_amount} />}
          info={info?.active_billable_amount?.info}
        />
        <EvtStatsCard
          label="Billables Net Cost"
          value={<DisplayAmount value={stats?.active_billable_net_cost} />}
          info={info?.active_billable_net_cost?.info}
        />
      </>
    ),
    revenue: (
      <>
        <EvtStatsCard
          label="Billable"
          value={<DisplayAmount value={stats?.billable_revenue} />}
          description={
            <>
              Billable period • <DisplayDays value={stats?.billable_period} />
            </>
          }
          info={info?.billable_revenue?.info}
        />
        <EvtStatsCard
          label="Billed"
          value={<DisplayAmount value={stats?.billed_revenue} />}
          description={
            <>
              Billed period • <DisplayDays value={stats?.billed_period} />
            </>
          }
          info={info?.billed_revenue?.info}
        />
        <EvtStatsCard
          label="Remaining Budget"
          value={<DisplayAmount value={stats?.remaining_budget} />}
          info={info?.remaining_budget?.info}
        />
        <EvtStatsCard
          label="Prepaid"
          value={<DisplayAmount value={stats?.prepaid} />}
          info={info?.prepaid?.info}
        />
        <EvtStatsCard
          label="Inventory"
          value={<DisplayAmount value={stats?.inventory} />}
          info={info?.inventory?.info}
        />
        <EvtStatsCard
          label="Backlog"
          value={<DisplayAmount value={stats?.backlog} />}
          info={info?.backlog?.info}
        />
      </>
    ),
    delivery: (
      <>
        <EvtStatsCard
          label="Approved vs Logged"
          value={
            <>
              <DisplayHours value={stats?.approved_hours} hoursOnly /> /{" "}
              <DisplayHours value={stats?.logged_hours} hoursOnly />
            </>
          }
          info={info?.approved_vs_logged?.info}
        />
      </>
    ),
  });

  const getCards = () => {
    switch (type) {
      case "fixed":
        return renderFixedCards(data);
      case "hourly":
        return renderHourlyCards(data);
      case "dedicated":
        return renderDedicatedCards(data);
      case "bucket":
        return renderBucketCards(data);
      case "recurring":
        return renderRecurringCards(data);
      default:
        return {};
    }
  };

  const { performance, activebillablesstatus, revenue, delivery } = getCards();

  return (
    <>
      <BillableHoursWarning validation={data?.validation} />
      {performance && (
        <section>
          <EvtSectionHeader title="Performance" />
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
            {performance}
          </div>
        </section>
      )}

      {canReadAmount && activebillablesstatus && (
        <section>
          <EvtSectionHeader title="Active Billable Status" />
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
            {activebillablesstatus}
          </div>
        </section>
      )}

      {canReadAmount && revenue && (
        <section>
          <EvtSectionHeader title="Revenue" />
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
            {revenue}
          </div>
        </section>
      )}

      {delivery && (
        <section>
          <EvtSectionHeader title="Delivery" />
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
            {delivery}
          </div>
        </section>
      )}
    </>
  );
};
